import { LoginAnnouncements } from '../components/LoginAnnouncements';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, Building2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { FEATURES } from '../lib/features';
import { markScreenStart } from '../lib/perfScreen';

const LoginPage = () => {
  const isLogin = true;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const msg = sessionStorage.getItem('auth-message');
    if (msg) { sessionStorage.removeItem('auth-message'); toast.warning(msg); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        markScreenStart('giris');
        await login(email, password);
        toast.success('Giriş başarılı!');
      } else {
        await register(email, password, name);
        toast.success('Kayıt başarılı!');
      }
      const back = sessionStorage.getItem('after_login');
      sessionStorage.removeItem('after_login');
      navigate(back || '/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Bir hata oluştu';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - İSG Permit tanıtımı (bizim alanımız) */}
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{ backgroundImage: 'url(/images/mall-portrait.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="login-left-panel"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/70 to-slate-900/40" />
        <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-slate-950/80 to-transparent" />
        <div className="relative z-10 flex w-full flex-col p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold" data-testid="login-brand">İSG Permit</h2>
              <p className="text-slate-200 text-sm">İş Sağlığı ve Güvenliği Yönetim Platformu</p>
            </div>
          </div>
          <div className="flex flex-1 items-center" data-testid="login-hero">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold leading-tight xl:text-6xl">Sahadaki her çalışma<br /><span className="text-orange-400">güvenli ve kayıt altında</span></h1>
              <div className="mt-8 grid grid-cols-3 gap-x-5 gap-y-3.5" data-testid="login-features">
                {FEATURES.map(([I, t], i) => (
                  <p key={t} className="flex items-start gap-2 text-sm font-medium leading-snug text-slate-100" data-testid={`login-feature-${i}`}><I className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />{t}</p>
                ))}
              </div>
              <button type="button" onClick={() => navigate('/tanitim')} data-testid="login-promo-link"
                className="group mt-10 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-6 py-3 text-base font-semibold backdrop-blur-md transition-colors hover:bg-white/20">
                İSG Permit Hakkında Bilgi, Biz kimiz?
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 bg-slate-50 overflow-y-auto">
        <Card className="w-full max-w-md border-slate-200 shadow-sm">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg">İSG Permit</span>
            </div>
            <CardTitle className="text-2xl font-bold">
              {isLogin ? 'Giriş Yap' : 'Kayıt Ol'}
            </CardTitle>
            <CardDescription>
              {isLogin 
                ? 'Hesabınıza giriş yapın' 
                : 'Yeni bir hesap oluşturun'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Ad Soyad</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Adınız Soyadınız"
                    required={!isLogin}
                    data-testid="register-name-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Şifre</Label>
                  <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs text-slate-500 hover:text-slate-900 hover:underline" data-testid="forgot-password-link">Şifremi Unuttum</button>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-slate-900 hover:bg-slate-800"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                    {isLogin ? 'Giriş yapılıyor...' : 'Kayıt olunuyor...'}
                  </span>
                ) : (
                  isLogin ? 'Giriş Yap' : 'Kayıt Ol'
                )}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <button
                type="button"
                className="text-sm text-slate-600 hover:text-slate-900"
                onClick={() => navigate('/register-company')}
                data-testid="toggle-auth-mode"
              >
                Hesabınız yok mu? <span className="font-semibold underline">Kayıt Ol</span>
              </button>
              <button type="button" className="mt-3 block w-full text-sm font-medium text-orange-600 hover:text-orange-700 lg:hidden" onClick={() => navigate('/tanitim')} data-testid="login-promo-link-mobile">
                İSG Permit Hakkında Bilgi, Biz kimiz?
              </button>
            </div>
          </CardContent>
        </Card>
        <LoginAnnouncements />
      </div>
    </div>
  );
};

export default LoginPage;
